import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Bot,
  CalendarCheck2,
  CheckCircle2,
  Compass,
  MapPinned,
  Sparkles,
  Target,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";

const Page = styled.main`
  background:
    radial-gradient(900px at 14% 6%, rgba(245, 158, 11, 0.12), transparent 60%),
    radial-gradient(860px at 84% 12%, rgba(56, 189, 248, 0.14), transparent 62%),
    radial-gradient(900px at 50% 100%, rgba(34, 197, 94, 0.06), transparent 68%),
    ${({ theme }) => theme.colors.bg};
  overflow-x: hidden;
  margin-top: -72px;
`;

const Highlight = styled.span`
  background: linear-gradient(90deg, #f59e0b, #38bdf8, #22c55e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 900;
`;

const Hero = styled.section`
  padding: 210px 20px 120px;
`;

const HeroWrap = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1.02fr) minmax(340px, 0.98fr);
  gap: 48px;
  align-items: center;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const HeroLeft = styled.div`
  max-width: 640px;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.48);
  border: 1px solid rgba(148, 163, 184, 0.14);
  color: #e2e8f0;
  font-size: 13px;
  font-weight: 800;
`;

const Title = styled(motion.h1)`
  margin-top: 22px;
  font-size: clamp(42px, 6vw, 72px);
  line-height: 0.98;
  font-weight: 1000;
  letter-spacing: -0.04em;
  color: ${({ theme }) => theme.colors.text};
`;

const Sub = styled(motion.p)`
  margin-top: 24px;
  max-width: 620px;
  font-size: 18px;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ProofRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 28px;
`;

const ProofPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.46);
  border: 1px solid rgba(148, 163, 184, 0.14);
  color: #e2e8f0;
  font-size: 14px;
  font-weight: 700;
`;

const CTAGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 32px;
`;

const PrimaryCTA = styled(motion.button)`
  min-height: 56px;
  padding: 16px 24px;
  border-radius: 18px;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 900;
  color: #04111f;
  background: linear-gradient(135deg, #f59e0b, #facc15);
  box-shadow: 0 20px 44px rgba(245, 158, 11, 0.28);
`;

const SecondaryCTA = styled.button`
  min-height: 56px;
  padding: 16px 22px;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 800;
  color: #f8fafc;
  background: rgba(15, 23, 42, 0.48);
`;

const HeroRight = styled.div`
  position: relative;
`;

const PreviewShell = styled(motion.div)`
  position: relative;
  padding: 20px;
  border-radius: 30px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background:
    linear-gradient(180deg, rgba(10, 16, 28, 0.94), rgba(4, 8, 18, 0.96)),
    rgba(2, 6, 23, 0.82);
  box-shadow: 0 34px 100px rgba(2, 6, 23, 0.52);
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
`;

const PreviewTitle = styled.div`
  color: #f8fafc;
  font-size: 18px;
  font-weight: 900;
`;

const PreviewSub = styled.div`
  margin-top: 4px;
  color: #94a3b8;
  font-size: 13px;
`;

const PreviewBadge = styled.div`
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.14);
  border: 1px solid rgba(56, 189, 248, 0.2);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: 1.08fr 0.92fr;
  gap: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  padding: 18px;
  border-radius: 22px;
  background: rgba(15, 23, 42, 0.62);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const PanelLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #cbd5e1;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const PreviewList = styled.div`
  margin-top: 16px;
  display: grid;
  gap: 12px;
`;

const PreviewCard = styled.div`
  padding: 15px 16px;
  border-radius: 18px;
  border: 1px solid
    ${({ active }) =>
      active ? "rgba(245, 158, 11, 0.35)" : "rgba(148, 163, 184, 0.12)"};
  background: ${({ active }) =>
    active
      ? "linear-gradient(135deg, rgba(245, 158, 11, 0.14), rgba(56, 189, 248, 0.08))"
      : "rgba(2, 6, 23, 0.44)"};
`;

const PreviewCardTitle = styled.div`
  color: #f8fafc;
  font-size: 15px;
  font-weight: 800;
`;

const PreviewCardText = styled.div`
  margin-top: 6px;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.55;
`;

const StatBlock = styled.div`
  padding: 14px 0;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const StatValue = styled.div`
  color: #f8fafc;
  font-size: 28px;
  font-weight: 1000;
`;

const StatText = styled.div`
  margin-top: 6px;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.55;
`;

const Section = styled.section`
  padding: 110px 20px;
`;

const Container = styled.div`
  max-width: 1180px;
  margin: 0 auto;
`;

const SectionHeading = styled(motion.h2)`
  text-align: center;
  font-size: clamp(34px, 4.4vw, 54px);
  font-weight: 1000;
  line-height: 1.06;
  color: ${({ theme }) => theme.colors.text};
`;

const SectionSub = styled(motion.p)`
  max-width: 860px;
  margin: 22px auto 0;
  text-align: center;
  font-size: 18px;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Grid = styled(motion.div)`
  margin-top: 64px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.div)`
  padding: 28px;
  border-radius: 28px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(15, 23, 42, 0.52);
  backdrop-filter: blur(10px);
  box-shadow: 0 24px 70px rgba(2, 6, 23, 0.34);
`;

const IconWrap = styled.div`
  width: 62px;
  height: 62px;
  border-radius: 20px;
  display: grid;
  place-items: center;
  color: #fff;
  background: linear-gradient(135deg, #f59e0b, #38bdf8);
  box-shadow: 0 0 42px rgba(56, 189, 248, 0.24);
`;

const CardTitle = styled.h3`
  margin-top: 20px;
  color: ${({ theme }) => theme.colors.text};
  font-size: 22px;
  font-weight: 900;
`;

const CardText = styled.p`
  margin-top: 10px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 15.5px;
  line-height: 1.75;
`;

const ProofSection = styled(Section)`
  background:
    radial-gradient(760px at 16% 0%, rgba(245, 158, 11, 0.08), transparent 58%),
    radial-gradient(760px at 84% 18%, rgba(56, 189, 248, 0.1), transparent 60%);
`;

const ProofPanel = styled(motion.div)`
  margin-top: 56px;
  padding: 30px;
  border-radius: 32px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background:
    linear-gradient(180deg, rgba(10, 16, 28, 0.92), rgba(4, 8, 18, 0.94)),
    rgba(2, 6, 23, 0.84);
  box-shadow: 0 34px 100px rgba(2, 6, 23, 0.5);
`;

const ProofColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ProofCard = styled.div`
  padding: 22px;
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(15, 23, 42, 0.58);
`;

const ProofTitle = styled.div`
  color: #f8fafc;
  font-size: 18px;
  font-weight: 900;
`;

const BulletList = styled.div`
  margin-top: 16px;
  display: grid;
  gap: 12px;
`;

const Bullet = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  color: #cbd5e1;
  font-size: 14px;
  line-height: 1.65;
`;

const CTASection = styled.section`
  padding: 120px 20px 160px;
  text-align: center;
`;

const CTAPanel = styled(motion.div)`
  max-width: 920px;
  margin: 0 auto;
  padding: 42px 28px;
  border-radius: 32px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background:
    radial-gradient(600px at 50% 0%, rgba(245, 158, 11, 0.12), transparent 60%),
    radial-gradient(600px at 80% 20%, rgba(56, 189, 248, 0.12), transparent 62%),
    rgba(15, 23, 42, 0.54);
  backdrop-filter: blur(10px);
  box-shadow: 0 30px 90px rgba(2, 6, 23, 0.4);
`;

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: "easeOut" },
  },
};

const stagger = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.12,
    },
  },
};

const WHY_GENERIC_FAILS = [
  {
    icon: Compass,
    title: "Generic roadmaps ignore your reality",
    text: "Most roadmaps assume the same background, same time, and same target for everyone. Real learners do not start from the same place.",
  },
  {
    icon: MapPinned,
    title: "Information is not the same as execution",
    text: "A giant list of topics looks helpful, but it still leaves users guessing what to do this week and what to build today.",
  },
  {
    icon: Bot,
    title: "Consistency breaks without guidance",
    text: "When momentum drops, learners need structure, reminders, and support. That is where product systems beat static PDFs.",
  },
];

const PRODUCT_PROOF = [
  "A personalized roadmap is more valuable than a giant public checklist because it removes unnecessary options.",
  "The product turns one big career goal into a sequence people can actually follow: phase, week, and day.",
  "Roadmaps connect directly to execution, progress tracking, and mentor help instead of living as a static read-only page.",
];

const ROADMAP_PREVIEW = [
  {
    title: "Phase 1: Foundations",
    text: "Choose what matters first so the learner stops switching stacks every week.",
  },
  {
    title: "Week 3: Core React + API work",
    text: "Turn the bigger direction into one clear short-term focus.",
  },
  {
    title: "Day 18: Build a product detail flow",
    text: "The user always knows what to learn and what to build today.",
  },
];

export default function RoadmapsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [hasRoadmap, setHasRoadmap] = useState(false);

  useEffect(() => {
    if (!user?.uid) {
      setHasRoadmap(false);
      return;
    }

    const unsubRoadmap = onSnapshot(doc(db, "roadmaps", user.uid), (snap) => {
      setHasRoadmap(snap.exists());
    });

    return unsubRoadmap;
  }, [user?.uid]);

  const primaryCtaLabel =
    user && hasRoadmap ? "Open My Roadmap" : "Create My Free Roadmap";
  const primaryCtaRoute =
    user && hasRoadmap ? "/my-roadmap" : "/career-clarity-home";
  const finalCtaLabel =
    user && hasRoadmap ? "Go to My Roadmap" : "Create My Free Roadmap";

  return (
    <Page>
      <Hero>
        <HeroWrap>
          <HeroLeft>
            <Eyebrow>
              <Sparkles size={15} />
              Personalized roadmaps, not static internet templates
            </Eyebrow>

            <Title variants={fadeUp} initial="hidden" animate="show">
              Roadmaps should create <Highlight>clarity</Highlight>, not more
              noise.
            </Title>

            <Sub variants={fadeUp} initial="hidden" animate="show">
              `skilll.dev` does not dump 50 generic roadmaps on users. It builds
              one roadmap based on their direction, level, and time so they can
              move from confusion to consistent daily execution.
            </Sub>

            <ProofRow>
              <ProofPill>
                <MapPinned size={15} />
                Personalized direction
              </ProofPill>
              <ProofPill>
                <CalendarCheck2 size={15} />
                Day-by-day execution
              </ProofPill>
              <ProofPill>
                <Bot size={15} />
                Mentor-style support
              </ProofPill>
            </ProofRow>

            <CTAGroup>
              <PrimaryCTA
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => navigate(primaryCtaRoute)}
              >
                {primaryCtaLabel}
                <ArrowRight size={18} />
              </PrimaryCTA>

              <SecondaryCTA
                onClick={() =>
                  document.getElementById("product-proof")?.scrollIntoView({
                    behavior: "smooth",
                  })
                }
              >
                See product proof
              </SecondaryCTA>
            </CTAGroup>
          </HeroLeft>

          <HeroRight>
            <PreviewShell
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, ease: "easeOut" }}
            >
              <PreviewHeader>
                <div>
                  <PreviewTitle>Roadmap Preview</PreviewTitle>
                  <PreviewSub>How the product turns career goals into action</PreviewSub>
                </div>
                <PreviewBadge>Live system</PreviewBadge>
              </PreviewHeader>

              <PreviewGrid>
                <Panel>
                  <PanelLabel>
                    <Target size={14} />
                    Product Flow
                  </PanelLabel>

                  <PreviewList>
                    {ROADMAP_PREVIEW.map((item, index) => (
                      <PreviewCard key={item.title} active={index === 1}>
                        <PreviewCardTitle>{item.title}</PreviewCardTitle>
                        <PreviewCardText>{item.text}</PreviewCardText>
                      </PreviewCard>
                    ))}
                  </PreviewList>
                </Panel>

                <Panel>
                  <PanelLabel>
                    <Sparkles size={14} />
                    Why This Works
                  </PanelLabel>

                  <StatBlock>
                    <StatValue>1 clear path</StatValue>
                    <StatText>
                      The user stops juggling conflicting roadmap advice.
                    </StatText>
                  </StatBlock>
                  <StatBlock>
                    <StatValue>Daily focus</StatValue>
                    <StatText>
                      The system answers the most important question: what do I do today?
                    </StatText>
                  </StatBlock>
                  <StatBlock>
                    <StatValue>Real proof of work</StatValue>
                    <StatText>
                      Projects and execution matter more than passive topic collecting.
                    </StatText>
                  </StatBlock>
                </Panel>
              </PreviewGrid>
            </PreviewShell>
          </HeroRight>
        </HeroWrap>
      </Hero>

      <Section>
        <Container>
          <SectionHeading
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            Why generic roadmaps usually fail
          </SectionHeading>

          <SectionSub
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            The internet is full of roadmaps, but most of them stop at
            information. They do not adapt to the user, they do not drive
            execution, and they do not help sustain consistency.
          </SectionSub>

          <Grid
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-120px" }}
          >
            {WHY_GENERIC_FAILS.map((item) => {
              const Icon = item.icon;
              return (
                <Card
                  key={item.title}
                  variants={fadeUp}
                  whileHover={{ y: -8 }}
                  transition={{ type: "spring", stiffness: 150 }}
                >
                  <IconWrap>
                    <Icon size={28} />
                  </IconWrap>
                  <CardTitle>{item.title}</CardTitle>
                  <CardText>{item.text}</CardText>
                </Card>
              );
            })}
          </Grid>
        </Container>
      </Section>

      <ProofSection id="product-proof">
        <Container>
          <SectionHeading
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            Product proof, not just roadmap talk
          </SectionHeading>

          <SectionSub
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            `skilll.dev` treats the roadmap like part of a larger system:
            planning, daily action, consistency, and support all work together.
          </SectionSub>

          <ProofPanel
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
          >
            <ProofColumns>
              <ProofCard>
                <ProofTitle>What the product is proving</ProofTitle>
                <BulletList>
                  {PRODUCT_PROOF.map((item) => (
                    <Bullet key={item}>
                      <CheckCircle2 size={16} color="#86efac" style={{ marginTop: 2 }} />
                      <span>{item}</span>
                    </Bullet>
                  ))}
                </BulletList>
              </ProofCard>

              <ProofCard>
                <ProofTitle>What users actually need</ProofTitle>
                <BulletList>
                  <Bullet>
                    <CheckCircle2 size={16} color="#7dd3fc" style={{ marginTop: 2 }} />
                    <span>One roadmap that fits their level and target role</span>
                  </Bullet>
                  <Bullet>
                    <CheckCircle2 size={16} color="#7dd3fc" style={{ marginTop: 2 }} />
                    <span>Daily guidance so they stop wasting energy deciding what to do</span>
                  </Bullet>
                  <Bullet>
                    <CheckCircle2 size={16} color="#7dd3fc" style={{ marginTop: 2 }} />
                    <span>Project-driven progress they can turn into portfolio and resume proof</span>
                  </Bullet>
                  <Bullet>
                    <CheckCircle2 size={16} color="#7dd3fc" style={{ marginTop: 2 }} />
                    <span>Consistency support when motivation drops or confusion returns</span>
                  </Bullet>
                </BulletList>
              </ProofCard>
            </ProofColumns>
          </ProofPanel>
        </Container>
      </ProofSection>

      <CTASection>
        <CTAPanel
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          <SectionHeading as="h2">
            Stop collecting roadmaps.
            <br />
            Start following one that fits <Highlight>you</Highlight>.
          </SectionHeading>

          <SectionSub>
            If the goal is real progress, the answer is not more lists. The
            answer is a clearer system with daily execution and product-backed
            guidance.
          </SectionSub>

          <CTAGroup style={{ justifyContent: "center" }}>
            <PrimaryCTA
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate(primaryCtaRoute)}
            >
              {finalCtaLabel}
              <ArrowRight size={18} />
            </PrimaryCTA>
          </CTAGroup>
        </CTAPanel>
      </CTASection>
    </Page>
  );
}
